/**
 * Periodo activo del dashboard de Envíos (SPEI).
 *
 * TypeScript puro: cero imports de Angular. Aquí viven las dos decisiones que
 * una pantalla de dinero no puede delegar en la disciplina de quien edita el
 * componente — cómo se formatea un día y qué rango es utilizable — más la
 * identidad con la que la consulta se deduplica.
 */

/** Los cuatro periodos que produce el mapa de chips. */
export type DashboardFixedPeriod = 'hoy' | 'semana' | 'mes' | 'año';

/** El valor que viaja en el parámetro `period` de la consulta. */
export type DashboardPeriodName = DashboardFixedPeriod | 'custom';

/**
 * Periodo activo como unión discriminada.
 *
 * Un `custom` NO se puede construir sin sus dos fechas: lo impide el compilador
 * con `strict`. Esa es justamente la protección — la identidad de la consulta se
 * deriva de ESTE valor, así que calcularla "olvidando" el rango deja de ser un
 * error que compila. Con dos parámetros opcionales sueltos (`dateFrom?`,
 * `dateTo?`) el estado "custom sin fechas" sería representable y la trampa
 * seguiría viva.
 */
export type DashboardPeriodSelection =
  | { kind: DashboardFixedPeriod }
  | { kind: 'custom'; from: string; to: string };

/**
 * Periodo por omisión del dashboard de Envíos: AÑO (pedido de producto, y el
 * mismo default que el back aplica cuando la petición no manda `period`).
 *
 * Vive acá y no en el componente porque hay TRES caminos que tienen que caer al
 * mismo sitio —el arranque, cancelar el selector de rango y un periodo guardado
 * que ya no sirve— y tres literales separados divergen al primer cambio.
 */
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriodSelection = { kind: 'año' };

/**
 * Día calendario LOCAL de una fecha, en `YYYY-MM-DD`.
 *
 * ⚠️ Nunca `toISOString()`: serializa en UTC. En México (UTC-6) la medianoche
 * local cae a las 06:00Z del mismo día y sale bien POR CASUALIDAD; en cualquier
 * máquina al este de UTC el 1-oct local se enviaría como `2026-09-30`, y como el
 * backend interpreta las fechas en hora de México la ventana quedaría corrida un
 * día. En un reporte de dinero eso es el rango equivocado, sin error y sin aviso.
 *
 * Falla CERRADA: una fecha inutilizable devuelve `null`, no una cadena. Sin esta
 * guarda un `Date` inválido produce `"NaN-NaN-NaN"`, que es una cadena de aspecto
 * plausible viajando hacia una consulta.
 */
export function formatLocalIsoDate(date: Date | null | undefined): string | null {
  if (!(date instanceof Date)) return null;
  if (!Number.isFinite(date.getTime())) return null;
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Único punto donde se decide si un rango es utilizable.
 *
 * Devuelve `null` —y entonces no hay consulta ni cambio de periodo activo—
 * cuando falta un extremo, cuando alguno no formatea, o cuando el día inicial es
 * posterior al final.
 *
 * ⚠️ La comparación va sobre los valores YA FORMATEADOS, no sobre los objetos
 * `Date`. Comparar `Date` trataría como invertido un rango del 1-oct 23:00 al
 * 1-oct 08:00, que es el MISMO día calendario y es legítimo (el backend cubre el
 * día completo). `YYYY-MM-DD` ordena lexicográficamente igual que
 * cronológicamente, así que comparar cadenas es exacto.
 *
 * Un rango invertido se RECHAZA, no se normaliza intercambiando extremos:
 * corregir en silencio lo que el operador pidió le devolvería un reporte de un
 * rango que él no eligió.
 */
export function buildCustomPeriod(
  start: Date | null | undefined,
  end: Date | null | undefined,
): DashboardPeriodSelection | null {
  const from = formatLocalIsoDate(start);
  const to = formatLocalIsoDate(end);
  if (!from || !to) return null;
  if (from > to) return null;
  return { kind: 'custom', from, to };
}

/**
 * Valor del parámetro `period` y el que se le pasa a la gráfica de líneas.
 * SIEMPRE definido: ese es el punto — derivarlo del mapa de chips daba
 * `undefined` para cualquier periodo fuera del mapa.
 */
export function dashboardPeriodName(selection: DashboardPeriodSelection): DashboardPeriodName {
  return selection.kind;
}

/**
 * Parte de la identidad de la consulta que depende del periodo.
 *
 * Para un periodo fijo devuelve exactamente el valor de hoy (`'hoy'`, `'semana'`,
 * `'mes'`, `'año'`), así que la deduplicación del arranque no cambia de
 * comportamiento. Para un rango incluye las dos fechas: sin ellas, pedir
 * septiembre y luego octubre produciría la MISMA clave y la segunda consulta se
 * descartaría en silencio, dejando la pantalla con los datos del rango anterior.
 */
export function dashboardPeriodKey(selection: DashboardPeriodSelection): string {
  return selection.kind === 'custom'
    ? `custom:${selection.from}:${selection.to}`
    : selection.kind;
}

/* ── Persistencia del periodo activo (R18) ─────────────────────────────────
 *
 * Lo que se guarda es SOLO el periodo elegido: el nombre y, si es un rango, sus
 * dos días calendario. Ni CLABEs, ni importes, ni nada de la respuesta del
 * backend — es una preferencia de pantalla, no datos de dinero.
 */

/**
 * Clave del periodo activo en el almacenamiento de SESIÓN (nunca el local: una
 * preferencia de una sesión autenticada no sobrevive a la pestaña).
 */
export const DASHBOARD_PERIOD_STORAGE_KEY = 'spei_dashboard_period';

/**
 * Clave del periodo activo del dashboard de TPV (feature 153, R13). DISTINTA de la de
 * Envíos a propósito: cada dashboard recuerda su propio periodo y elegir un rango en uno
 * no puede cambiar lo que abre el otro. Mismo almacenamiento de SESIÓN y mismo formato.
 */
export const TPV_DASHBOARD_PERIOD_STORAGE_KEY = 'tpv_dashboard_period';

/** Los cuatro periodos fijos, como conjunto, para validar lo que se lee. */
const FIXED_PERIODS: ReadonlySet<string> = new Set<DashboardFixedPeriod>(['hoy', 'semana', 'mes', 'año']);

/** Serializa el periodo activo. La forma es la del propio tipo: `kind` + rango. */
export function serializeDashboardPeriod(selection: DashboardPeriodSelection): string {
  return selection.kind === 'custom'
    ? JSON.stringify({ kind: 'custom', from: selection.from, to: selection.to })
    : JSON.stringify({ kind: selection.kind });
}

/**
 * Día calendario local a partir de una cadena `YYYY-MM-DD`.
 *
 * ⚠️ Nunca `new Date('2026-09-01')`: una fecha ISO sin hora la interpreta la
 * especificación como UTC, así que en México daría el 31 de agosto a las 18:00 —
 * el mismo off-by-one que `toISOString()` por el otro lado.
 *
 * La vuelta por `formatLocalIsoDate` no es decorativa: `new Date(2026, 1, 31)`
 * NO falla, se desborda a marzo. Exigir que el valor formateado coincida con el
 * texto leído es lo que rechaza un 31 de febrero.
 */
function parseLocalIsoDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return formatLocalIsoDate(date) === value ? date : null;
}

/**
 * Ventana de consulta del selector de rango: de hoy hacia atrás un año, sin
 * futuro. Son los MISMOS dos valores que aplicaba `transfer-date-range-filter`
 * cuando el dashboard lo montaba (`maxDate` = hoy sin horas, `minDate` = hoy
 * menos un año).
 *
 * ⚠️ Está EXPORTADA, y no es un detalle: desde que el calendario vive dentro del
 * contenedor (R19), la ventana la tienen que aplicar DOS caminos —aplicar un
 * rango nuevo y restaurar el guardado— y dos copias de una compuerta de un
 * reporte de dinero divergen al primer cambio. Un periodo fijo no tiene rango
 * que acotar, así que siempre es elegible.
 *
 * `hoy` entra por parámetro para que la regla sea comprobable sin depender del
 * reloj de la máquina que corre las pruebas.
 */
export function isSelectableDashboardRange(
  selection: DashboardPeriodSelection,
  hoy: Date,
): boolean {
  if (selection.kind !== 'custom') return true;
  const maximo = formatLocalIsoDate(hoy);
  if (!maximo) return false;
  const piso = new Date(hoy.getTime());
  piso.setFullYear(piso.getFullYear() - 1);
  const minimo = formatLocalIsoDate(piso);
  if (!minimo) return false;
  return selection.from >= minimo && selection.to <= maximo;
}

/**
 * Lee el periodo guardado y lo devuelve SOLO si sigue siendo elegible hoy.
 *
 * Falla CERRADA (`null` ⇒ el que llama cae al periodo por omisión) ante: nada
 * guardado, JSON que no parsea, algo que no es un objeto, un `kind` que no
 * existe, un `custom` sin sus dos fechas, un rango incompleto o invertido —lo
 * juzga `buildCustomPeriod`, la MISMA función que valida una selección nueva— y
 * un rango que hoy ya no se podría elegir en el selector: con fecha futura
 * (porque "hoy" avanzó mientras la pestaña seguía abierta) o fuera de la ventana
 * de 12 meses.
 *
 * `hoy` entra por parámetro para que la regla sea comprobable sin depender del
 * reloj de la máquina que corre las pruebas.
 */
export function parseStoredDashboardPeriod(
  raw: string | null | undefined,
  hoy: Date,
): DashboardPeriodSelection | null {
  if (typeof raw !== 'string' || !raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Un valor corrupto no puede tumbar el dashboard: se ignora y ya.
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const { kind, from, to } = parsed as { kind?: unknown; from?: unknown; to?: unknown };
  if (typeof kind !== 'string') return null;

  if (FIXED_PERIODS.has(kind)) return { kind: kind as DashboardFixedPeriod };
  if (kind !== 'custom') return null;

  const seleccion = buildCustomPeriod(parseLocalIsoDate(from), parseLocalIsoDate(to));
  if (!seleccion || seleccion.kind !== 'custom') return null;
  if (!isSelectableDashboardRange(seleccion, hoy)) return null;
  return seleccion;
}
